// frontend/src/pages/shop/Dashboard.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getShopStats } from '../../services/shopService';
import ShopLayout from '../../components/layout/ShopLayout';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Бодит өгөгдөл
  const [recentDeliveries, setRecentDeliveries] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [codStats, setCodStats] = useState({
    totalCod: 0,
    confirmedAmount: 0,
    pendingAmount: 0,
    balance: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // 1. Үндсэн статистик
      const response = await getShopStats();
      if (response.success) {
        setStats(response.data);
      } else {
        setError(response.message || 'Статистик татахад алдаа гарлаа');
      }

      // 2. Сүүлийн хүргэлтүүд (5 ширхэг)
      try {
        const deliveriesResponse = await fetch(`/api/v1/shop/deliveries?limit=5&date_range=week`, { headers });
        const deliveriesData = await deliveriesResponse.json();
        if (deliveriesData.success) {
          setRecentDeliveries(deliveriesData.data?.slice(0, 5) || []);
        }
      } catch (e) {
        console.log('Recent deliveries not available:', e);
      }

      // 3. Шилдэг барааууд
      try {
        const productsResponse = await fetch(`/api/v1/shop/products?limit=5`, { headers });
        const productsData = await productsResponse.json();
        if (productsData.success) {
          const products = productsData.data || [];
          const sortedProducts = products
            .sort((a, b) => (b.stock || 0) - (a.stock || 0))
            .slice(0, 5)
            .map(p => ({
              id: p.id,
              name: p.product_name,
              stock: p.stock || 0,
              price: p.price || 0,
              image: getProductEmoji(p.category)
            }));
          setTopProducts(sortedProducts);
        }
      } catch (e) {
        console.log('Products not available:', e);
      }

      // 4. COD тооцоо
      try {
        const codResponse = await fetch(`/api/v1/shop/cod-payments?date_range=month`, { headers });
        const codData = await codResponse.json();
        if (codData.success) {
          setCodStats({
            totalCod: codData.data.summary?.total_amount || 0,
            confirmedAmount: codData.data.summary?.confirmed_amount || 0,
            pendingAmount: codData.data.summary?.pending_amount || 0,
            balance: codData.data.summary?.balance || 0
          });
        }
      } catch (e) {
        console.log('COD stats not available:', e);
      }

    } catch (err) {
      console.error('❌ Dashboard өгөгдөл татахад алдаа:', err);
      setError(err.response?.data?.message || err.message || 'Статистик татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  // Бараа категориар emoji
  const getProductEmoji = (category) => {
    const emojis = {
      'clothing': '👕',
      'electronics': '📱',
      'shoes': '👟',
      'accessories': '👜',
      'food': '🍔',
      'beauty': '💄',
      'home': '🏠',
      'sports': '⚽'
    };
    return emojis[category] || '📦';
  };

  // Статус badge
  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Хүлээгдэж буй', color: 'bg-yellow-100 text-yellow-700' },
      assigned: { label: 'Хувиарласан', color: 'bg-blue-100 text-blue-700' },
      in_transit: { label: 'Хүргэж байгаа', color: 'bg-indigo-100 text-indigo-700' },
      delivered: { label: 'Хүргэсэн', color: 'bg-green-100 text-green-700' },
      cancelled: { label: 'Цуцалсан', color: 'bg-red-100 text-red-700' },
      postponed: { label: 'Хойшилсон', color: 'bg-orange-100 text-orange-700' },
    };
    const badge = badges[status] || badges.pending;
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>{badge.label}</span>;
  };

  // Цаг форматлах
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Дөнгөж сая';
    if (diffMins < 60) return `${diffMins} минутын өмнө`;
    if (diffHours < 24) return `${diffHours} цагийн өмнө`;
    if (diffDays < 7) return `${diffDays} өдрийн өмнө`;
    return date.toLocaleDateString('mn-MN');
  };

  // Calculations
  const pendingDeliveries = stats?.today_deliveries?.pending || 0;
  const totalDeliveries = stats?.today_deliveries?.total || 0;
  const completedDeliveries = stats?.today_deliveries?.delivered || 0;
  const inTransitDeliveries = stats?.today_deliveries?.in_transit || 0;
  const successRate = totalDeliveries > 0 
    ? Math.round((completedDeliveries / totalDeliveries) * 100) 
    : 0;

  return (
    <ShopLayout>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Ачааллаж байна...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center bg-red-50 border-2 border-red-200 rounded-xl p-8 max-w-md">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-bold text-red-800 mb-2">Алдаа гарлаа</h3>
            <p className="text-red-700 mb-4">{error}</p>
            <button 
              onClick={fetchDashboardData}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
            >
              Дахин оролдох
            </button>
          </div>
        </div>
      ) : !stats ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg mb-4">Статистик олдсонгүй</p>
          <button 
            onClick={fetchDashboardData}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            Дахин оролдох
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ============================================ */}
          {/* HEADER */}
          {/* ============================================ */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Тавтай морилно уу! 👋</h1>
              <p className="text-gray-600 mt-1">Өнөөдрийн борлуулалт ба захиалгууд</p>
            </div>
            <button 
              onClick={fetchDashboardData}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:shadow-md transition text-gray-700 font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Шинэчлэх</span>
            </button>
          </div>

          {/* ============================================ */}
          {/* STATS CARDS */}
          {/* ============================================ */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1: Бараа */}
            <div 
              className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => navigate('/shop/products')}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <span className="text-3xl">📦</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-100">Нийт бараа</p>
                  <p className="text-3xl font-bold">{stats.products?.total || 0}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-blue-100">Үлдэгдэл:</span>
                  <span className="font-semibold">{stats.products?.stock || 0} ш</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-blue-100">Үнийн дүн:</span>
                  <span className="font-semibold">{(stats.products?.value || 0).toLocaleString()}₮</span>
                </div>
              </div>
            </div>

            {/* Card 2: Өнөөдрийн хүргэлт */}
            <div 
              className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => navigate('/shop/deliveries')}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <span className="text-3xl">🚚</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-green-100">Өнөөдөр</p>
                  <p className="text-3xl font-bold">{totalDeliveries}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-100">✅ Хүргэсэн:</span>
                  <span className="font-semibold">{completedDeliveries}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-100">🚚 Хүргэж байна:</span>
                  <span className="font-semibold">{inTransitDeliveries}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-100">⏰ Хүлээгдэж буй:</span>
                  <span className="font-semibold">{pendingDeliveries}</span>
                </div>
              </div>
              <div className="mt-3 w-full bg-white/20 rounded-full h-2">
                <div 
                  className="bg-white h-2 rounded-full transition-all duration-500" 
                  style={{ width: `${successRate}%` }}
                ></div>
              </div>
              <p className="text-xs text-green-100 mt-1 text-right">{successRate}% амжилттай</p>
            </div>

            {/* Card 3: COD авах үлдэгдэл */}
            <div 
              className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => navigate('/shop/cod-payments')}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <span className="text-3xl">💰</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-purple-100">Авах үлдэгдэл</p>
                  <p className="text-2xl font-bold">{codStats.balance.toLocaleString()}₮</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-purple-100">Нийт COD:</span>
                  <span className="font-semibold">{codStats.totalCod.toLocaleString()}₮</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-purple-100">Баталгаажсан:</span>
                  <span className="font-semibold">{codStats.confirmedAmount.toLocaleString()}₮</span>
                </div>
                {codStats.pendingAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-purple-100">Хүлээгдэж буй:</span>
                    <span className="font-semibold">{codStats.pendingAmount.toLocaleString()}₮</span>
                  </div>
                )}
              </div>
            </div>

            {/* Card 4: Хүргэлтийн төлбөр */}
            <div 
              className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => navigate('/shop/settlements')}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <span className="text-3xl">🧾</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-orange-100">Хүргэлтийн төлбөр</p>
                  <p className="text-2xl font-bold">{(stats.delivery_fees?.estimated_fee || 0).toLocaleString()}₮</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-orange-100">Нийт хүргэлт:</span>
                  <span className="font-semibold">{stats.delivery_fees?.total_deliveries || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-orange-100">Нэг хүргэлт:</span>
                  <span className="font-semibold">{(stats.delivery_fees?.fee_per_delivery || 5000).toLocaleString()}₮</span>
                </div>
              </div>
            </div>
          </div>

          {/* ============================================ */}
          {/* QUICK ACTIONS */}
          {/* ============================================ */}
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <span className="text-2xl">⚡</span>
                  <span>Түргэн үйлдлүүд</span>
                </h3>
                <p className="text-sm text-indigo-100">Ямар үйлдэл хийх вэ?</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => navigate('/shop/deliveries/create')} 
                  className="flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 rounded-xl hover:shadow-lg transition-all duration-300 font-semibold transform hover:-translate-y-1"
                >
                  <span className="text-xl">📦</span>
                  <span>Хүргэлт шивэх</span>
                </button>
                <button 
                  onClick={() => navigate('/shop/deliveries')} 
                  className="flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-xl hover:shadow-lg transition-all duration-300 font-semibold transform hover:-translate-y-1"
                >
                  <span className="text-xl">🚚</span>
                  <span>Хүргэлтүүд</span>
                </button>
                <button 
                  onClick={() => navigate('/shop/cod-payments')} 
                  className="flex items-center gap-2 px-6 py-3 bg-white text-pink-600 rounded-xl hover:shadow-lg transition-all duration-300 font-semibold transform hover:-translate-y-1"
                >
                  <span className="text-xl">💵</span>
                  <span>COD төлбөр</span>
                </button>
              </div>
            </div>
          </div>

          {/* ============================================ */}
          {/* BOTTOM SECTION - 2 COLUMNS */}
          {/* ============================================ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Deliveries - БОДИТ ӨГӨГДӨЛ */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">📋</span>
                  <span>Сүүлийн хүргэлтүүд</span>
                </h3>
                <button 
                  onClick={() => navigate('/shop/deliveries')}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Бүгдийг харах →
                </button>
              </div>
              {recentDeliveries.length > 0 ? (
                <div className="space-y-3">
                  {recentDeliveries.map((delivery) => (
                    <div 
                      key={delivery.id} 
                      className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition cursor-pointer border border-gray-100"
                      onClick={() => navigate(`/shop/deliveries/${delivery.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{delivery.delivery_number}</p>
                        <p className="text-xs text-gray-500 truncate">{delivery.customer_name} • {delivery.customer_phone}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatTime(delivery.created_at)}</p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="text-sm font-bold text-gray-900">{(delivery.cod_amount || 0).toLocaleString()}₮</p>
                        <div className="mt-1">
                          {getStatusBadge(delivery.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl mb-2 block">📭</span>
                  <p>Хүргэлт байхгүй</p>
                  <button 
                    onClick={() => navigate('/shop/deliveries/create')}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
                  >
                    Хүргэлт шивэх
                  </button>
                </div>
              )}
            </div>

            {/* Top Products - БОДИТ ӨГӨГДӨЛ */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">🏆</span>
                  <span>Барааны үлдэгдэл</span>
                </h3>
                <button 
                  onClick={() => navigate('/shop/products')}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Дэлгэрэнгүй →
                </button>
              </div>
              {topProducts.length > 0 ? (
                <div className="space-y-3">
                  {topProducts.map((product, index) => (
                    <div 
                      key={product.id} 
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition cursor-pointer border border-gray-100"
                      onClick={() => navigate(`/shop/products/${product.id}`)}
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                        <span className="text-2xl">{product.image}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{product.name}</p>
                        <p className="text-xs text-gray-500">Үлдэгдэл: {product.stock} ш</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{product.price.toLocaleString()}₮</p>
                        <p className="text-xs text-gray-500">#{index + 1}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl mb-2 block">📦</span>
                  <p>Бараа байхгүй</p>
                  <button 
                    onClick={() => navigate('/shop/products')}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
                  >
                    Бараа нэмэх
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </ShopLayout>
  );
};

export default Dashboard;